import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Modal,
  Stack,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconFileText,
  IconSettings,
} from "@tabler/icons-react";
import { useFormTemplates } from "../../context/FormTemplatesContext";
import { selectedRowStyle } from "./selectedRowStyle";
import {
  AssignFormPayload,
  ClientFormSourceType,
  FormTemplate,
} from "../../types/formTemplates";

interface AssignFormModalProps {
  opened: boolean;
  onClose: () => void;
  /** Assign one or more templates to the service in a single action. */
  onAssign: (payloads: AssignFormPayload[]) => Promise<void> | void;
  isAssigning?: boolean;
  /**
   * Composite `${source_type}:${source_id}` keys for templates already
   * attached to the service (saved + pending). Attached templates are hidden
   * from the picker so they can't be added twice.
   */
  attachedKeys?: ReadonlySet<string>;
}

type SourceKind = "custom" | "standard";

const EMPTY_KEYS: ReadonlySet<string> = new Set();

/** The source_type a template maps to when attached as a required form. */
const sourceTypeOf = (t: FormTemplate): ClientFormSourceType =>
  t.source === "custom" ? "salon_template" : "template";

/** Composite identity key matching RequiredForm/PendingRequiredForm keys. */
const keyOf = (t: FormTemplate): string =>
  `${sourceTypeOf(t)}:${t.slug || t.id}`;

export const AssignFormModal: React.FC<AssignFormModalProps> = ({
  opened,
  onClose,
  onAssign,
  isAssigning = false,
  attachedKeys = EMPTY_KEYS,
}) => {
  const { standardTemplates, customTemplates, fetchTemplates } =
    useFormTemplates();

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [tab, setTab] = useState<SourceKind>("custom");
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      fetchTemplates().catch(() => {
        /* errors surface via context */
      });
    }
    // Reset transient picker state whenever the modal opens OR closes, so a
    // Cancel/backdrop dismiss → reopen never briefly flashes a stale selection.
    setSelectedKeys([]);
    setSearch("");
    setError(null);
  }, [opened, fetchTemplates]);

  // Standards with is_mock_placeholder are not assignable. Templates already
  // attached to the service are hidden so they can't be added twice.
  // post_appointment templates use their own picker, not this one.
  const standards = useMemo(
    () =>
      standardTemplates.filter(
        (t) =>
          !t.is_mock_placeholder &&
          !attachedKeys.has(keyOf(t)) &&
          t.category !== "post_appointment",
      ),
    [standardTemplates, attachedKeys],
  );
  const customs = useMemo(
    () =>
      customTemplates.filter(
        (t) => !attachedKeys.has(keyOf(t)) && t.category !== "post_appointment",
      ),
    [customTemplates, attachedKeys],
  );

  const filter = (list: FormTemplate[]) =>
    search.trim()
      ? list.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()))
      : list;

  const active = tab === "custom" ? filter(customs) : filter(standards);

  // Resolve selected composite keys back to their templates for assignment.
  const byKey = useMemo(() => {
    const map = new Map<string, FormTemplate>();
    [...standards, ...customs].forEach((t) => map.set(keyOf(t), t));
    return map;
  }, [standards, customs]);

  // Keep the selection in sync with what's actually available. If a selected
  // template stops being shown — e.g. it became attached via the previous
  // (partially failed) assign, or the template list changed underneath us —
  // drop its key so "Assign N" never silently fires fewer than N.
  useEffect(() => {
    setSelectedKeys((prev) => {
      const pruned = prev.filter((k) => byKey.has(k));
      if (pruned.length !== prev.length) {
        // eslint-disable-next-line no-console
        console.debug(
          `[AssignFormModal] dropped ${
            prev.length - pruned.length
          } selection(s) no longer available`,
        );
        return pruned;
      }
      return prev;
    });
  }, [byKey]);

  const customCount = selectedKeys.filter((k) =>
    k.startsWith("salon_template:"),
  ).length;
  const standardCount = selectedKeys.length - customCount;
  const selectionSummary =
    selectedKeys.length === 0
      ? "Select one or more templates"
      : `${[
          customCount ? `${customCount} custom` : null,
          standardCount ? `${standardCount} standard` : null,
        ]
          .filter(Boolean)
          .join(" + ")} selected`;

  const handleAssign = async () => {
    const payloads: AssignFormPayload[] = selectedKeys
      .map((k) => byKey.get(k))
      .filter((t): t is FormTemplate => Boolean(t))
      .map((t) => ({
        source_type: sourceTypeOf(t),
        source_id: t.slug || t.id,
      }));
    if (payloads.length === 0) return;
    try {
      await onAssign(payloads);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not assign forms.");
    }
  };

  const emptyMessage =
    tab === "custom"
      ? search.trim()
        ? "No custom templates match."
        : customTemplates.length > 0
          ? "All custom templates are already attached."
          : "No custom templates yet. Create one from the template library first."
      : search.trim()
        ? "No standard templates match."
        : "All standard templates are already attached.";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={700}>Assign a form</Text>}
      size="lg"
      centered
    >
      <Stack gap="md">
        <Tabs value={tab} onChange={(v) => v && setTab(v as SourceKind)}>
          <Tabs.List>
            <Tabs.Tab value="custom">Custom templates</Tabs.Tab>
            <Tabs.Tab value="standard">Standard templates</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        <TextInput
          placeholder="Search by name"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          size="xs"
        />

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={14} />}>
            {error}
          </Alert>
        )}

        <Box
          style={{
            maxHeight: 360,
            overflowY: "auto",
            padding: 4,
            border: "1px solid var(--mantine-color-gray-2)",
            borderRadius: 6,
          }}
        >
          {active.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center" p="md">
              {emptyMessage}
            </Text>
          ) : (
            <Checkbox.Group value={selectedKeys} onChange={setSelectedKeys}>
              <Stack gap={0}>
                {active.map((t) => {
                  const key = keyOf(t);
                  const Icon =
                    t.source === "custom" ? IconSettings : IconFileText;
                  return (
                    <Box
                      key={key}
                      p="xs"
                      onClick={() =>
                        setSelectedKeys((prev) =>
                          prev.includes(key)
                            ? prev.filter((k) => k !== key)
                            : [...prev, key],
                        )
                      }
                      style={selectedRowStyle(selectedKeys.includes(key))}
                    >
                      <Group gap="xs" wrap="nowrap" align="flex-start">
                        <Checkbox
                          value={key}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Icon
                          size={18}
                          color={
                            t.source === "custom"
                              ? "var(--brand-burnt-orange)"
                              : "var(--mantine-color-teal-6)"
                          }
                        />
                        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                          <Group gap={4} wrap="nowrap">
                            <Text size="sm" fw={600} truncate>
                              {t.name}
                            </Text>
                            <Badge
                              size="xs"
                              variant="light"
                              color="gray"
                              style={{ textTransform: "none" }}
                            >
                              v{t.version}
                            </Badge>
                          </Group>
                          {t.description && (
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {t.description}
                            </Text>
                          )}
                          <Text size="xs" c="dimmed">
                            {t.field_count} fields
                          </Text>
                        </Stack>
                      </Group>
                    </Box>
                  );
                })}
              </Stack>
            </Checkbox.Group>
          )}
        </Box>

        <Group justify="space-between" align="center">
          <Text size="xs" c="dimmed">
            {selectionSummary}
          </Text>
          <Group gap="xs">
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="var(--brand-teal)"
              disabled={selectedKeys.length === 0}
              loading={isAssigning}
              onClick={handleAssign}
            >
              {selectedKeys.length > 1
                ? `Assign ${selectedKeys.length} forms`
                : "Assign"}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};
